import { Link } from 'react-router-dom';
import { Sparkles, Users, Heart, Utensils, ArrowRight } from 'lucide-react';

// Local image paths (bundled with app)
const IMAGES = {
  hero: "/images/hero-children.jpeg",
  founder: "/images/founder-brianna.jpeg",
  event1: "/images/children-hugging.jpeg",
  event2: "/images/event-stage.jpeg",
};

const pillars = [
  {
    icon: Sparkles,
    title: "Building Unstoppable Confidence",
    description: "Through talent showcases and competitions—beauty pageants, dance, singing, art, and poetry—children perform in front of thousands.",
    color: "bg-[#FACC15]",
    borderClass: "pillar-confidence"
  },
  {
    icon: Users,
    title: "Mentorship & Growth",
    description: "Safe spaces for essential conversations. Sessions for girls on womanhood and guidance for boys on becoming responsible, empathetic men.",
    color: "bg-[#38BDF8]",
    borderClass: "pillar-mentorship"
  },
  {
    icon: Heart,
    title: "Joy & Community",
    description: "Life should be celebrated. Games, activities, and a festive environment where children can simply be children.",
    color: "bg-[#FF7A59]",
    borderClass: "pillar-joy"
  },
  {
    icon: Utensils,
    title: "Nourishment",
    description: "No child can dream on an empty stomach. Every event ensures all children are provided with nutritious food.",
    color: "bg-[#22C55E]",
    borderClass: "pillar-nourishment"
  }
];

const HomePage = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.hero} 
            alt="Children at Fun in the Sun event" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 image-overlay"></div>
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-2xl">
            <span className="inline-block font-accent text-2xl text-[#FF7A59] mb-4">
              Empowering the next generation
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1E293B] font-['Fredoka'] leading-tight mb-6">
              Helping Children in Kenya Through{' '}
              <span className="text-[#FACC15]">Joy</span> and{' '}
              <span className="text-[#38BDF8]">Community</span>
            </h1>
            <p className="text-lg md:text-xl text-[#64748B] mb-10 leading-relaxed">
              Protopia believes a child's circumstances should never define their potential. 
              We're creating a movement across Kenya to help every child thrive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/donate"
                data-testid="hero-donate-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 shadow-[0_4px_14px_0_rgba(250,204,21,0.39)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.23)] hover:-translate-y-1 transition-all duration-200"
              >
                Donate Now
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E2E8F0] bg-white/80 backdrop-blur text-[#1E293B] font-bold px-8 py-4 hover:bg-white transition-all duration-200"
              >
                Learn Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-2xl text-[#FF7A59]">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
                From Grandmother's Legacy to Kenya's Future
              </h2>
              <p className="text-lg text-[#64748B] leading-relaxed mb-6">
                Protopia was born from founder Brianna Thomas's desire to continue her grandmother 
                Wilma Jean's "Fun in the Sun" gatherings—and a profound experience with the Ronald 
                McDonald House when her daughter was born at just 22 weeks.
              </p>
              <p className="text-lg text-[#64748B] leading-relaxed mb-8">
                The kindness they received ignited a fire to do something "big for the world," 
                bringing the spirit of community and hope to the children of Kenya.
              </p>
              <Link
                to="/about"
                data-testid="about-read-more"
                className="inline-flex items-center gap-2 text-[#38BDF8] font-bold hover:gap-4 transition-all duration-200"
              >
                Read Full Story
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={IMAGES.founder} 
                  alt="Brianna Thomas, Founder of Protopia" 
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#FACC15] rounded-2xl p-6 shadow-lg">
                <p className="font-accent text-xl text-[#422006]">"The sky is only the limit if you can't see past it"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="py-24 md:py-32 bg-[#FDFBF7]" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#FF7A59]">Our Approach</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-4">
              Fun in the Sun: Four Pillars
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Our flagship program feeds the body, mind, and soul through a holistic "all-in-one" event approach.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {pillars.map((pillar, index) => (
              <div 
                key={index}
                data-testid={`pillar-${index}`}
                className={`rounded-3xl bg-white p-8 md:p-10 shadow-sm hover:shadow-md transition-all duration-300 ${pillar.borderClass}`}
              >
                <div className={`w-14 h-14 ${pillar.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
                  {pillar.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {pillar.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Gallery Preview */}
      <section className="py-24 md:py-32 bg-white" data-testid="gallery-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="font-accent text-2xl text-[#FF7A59]">Making a Difference</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-4">
              Moments of Joy
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img src={IMAGES.event1} alt="Children hugging at event" className="w-full h-80 object-cover" />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img src={IMAGES.event2} alt="Fun in the Sun stage" className="w-full h-80 object-cover" />
            </div>
            <div className="rounded-3xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <img src={IMAGES.hero} alt="Children at event" className="w-full h-80 object-cover" />
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/gallery"
              data-testid="view-gallery-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E2E8F0] bg-transparent text-[#1E293B] font-bold px-8 py-4 hover:bg-[#F3F4F6] transition-all duration-200"
            >
              View Full Gallery
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#38BDF8]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Fredoka'] mb-6">
            Join the Movement
          </h2>
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-10">
            We aren't just hosting an event; we are creating a movement across Kenya and the entire African continent.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/donate"
              data-testid="cta-donate-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 shadow-[0_4px_14px_0_rgba(250,204,21,0.39)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.23)] hover:-translate-y-1 transition-all duration-200"
            >
              Make a Donation
            </Link>
            <Link
              to="/volunteer"
              data-testid="cta-volunteer-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#1E293B] font-bold px-8 py-4 hover:bg-white/90 hover:-translate-y-1 transition-all duration-200"
            >
              Become a Volunteer
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
